/**
 * Opt-in error-reporting pipeline for TanStack queries.
 *
 * Pass-through behavior by default: queries that don't declare any
 * `meta.errorMessage` / `meta.captureExceptions` field are silent. A
 * query opts in by setting one of those fields on `useQuery({ meta })`
 * — the cache handler then fires Sentry (and a user-facing toast when
 * an `errorMessage` is provided) on the *single* fetch failure event
 * rather than re-firing on every observer update.
 *
 * Keep this module separate from the root layout so test renders can
 * mount the same pipeline without pulling in the rest of the bundle
 * (auth, routing, sample-account prefetching, etc.).
 */

import { QueryCache } from '@tanstack/react-query';
import type { Query } from '@tanstack/react-query';
import { snackbarApi } from '@/services/snackbar';
import { sentryCaptureException } from '@/utils/sentry';

// Open-ended meta shape: named fields for the error pipeline, plus an
// index signature so existing call sites that stash other things on
// `meta` (e.g. `usePaginatedQuery`'s `pagination` payload) still type.
// Type alias (not interface) so TanStack's
// `Register extends { queryMeta: infer T extends Record<string, unknown> }`
// constraint resolves to our shape rather than falling through to its
// default — interfaces are augmentable and TS can't prove they satisfy
// `Record<string, unknown>`.
// Only referenced internally for the `Register` augmentation — callers
// pass meta as object literals and rely on TanStack's typed `query.meta`
// for inference. Kept un-exported so fallow doesn't flag it.
type QueryErrorMeta = {
  /**
   * Toast copy shown to the user on fetch failure. Setting this also
   * implies `captureExceptions: true` — there's no case where we want
   * to surface a toast without reporting the underlying error.
   */
  errorMessage?: string;
  /**
   * Report fetch failures to Sentry without showing a toast. Useful
   * for background fetches that shouldn't interrupt the user but
   * still need observability.
   */
  captureExceptions?: boolean;
  /** Catch-all for ad-hoc meta payloads. Read with a narrowing cast. */
  [key: string]: unknown;
};

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: QueryErrorMeta;
    mutationMeta: QueryErrorMeta;
  }
}

/**
 * A side-effect run on every query failure, before the Sentry/snackbar
 * reporting. The app wires the auth-rejection handler in here (kept out of this
 * module so it stays free of the auth/routing bundle — see the file header).
 */
type OnQueryError = (error: unknown, query: Query<unknown, unknown>) => void;

/**
 * Build a fresh `QueryCache` wired to fire Sentry / snackbar on
 * meta-opted-in failures. Each `QueryClient` needs its own cache
 * (caches can't be shared across clients), so this is a factory.
 *
 * `onQueryError` is an optional side-effect run first on every failure (the app
 * passes the auth-rejection handler; test renders omit it).
 */
export function createAppQueryCache(onQueryError?: OnQueryError): QueryCache {
  return new QueryCache({
    onError: (error, query) => {
      onQueryError?.(error, query);

      const meta = query.meta;
      const shouldCapture = meta?.errorMessage != null || meta?.captureExceptions === true;
      if (!shouldCapture) return;

      sentryCaptureException(error, { extra: { queryKey: query.queryKey } });

      if (meta.errorMessage != null) {
        snackbarApi.create({
          variant: 'error',
          // Per-queryKey id so the same query failing twice in a row
          // doesn't stack identical toasts.
          id: `query.${JSON.stringify(query.queryKey)}.error`,
          message: meta.errorMessage,
        });
      }
    },
  });
}
